import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { withStyles } from '@material-ui/core/styles';
import {
  Button,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@material-ui/core';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { navigate } from '@reach/router';
import { Header } from '../../components/index';
import { POI_ACTION_TYPES, WS_ACTION_TYPES } from '../../redux-saga/actionTypes';
import { commands } from '../../constants';
import styles from './styles';

const { editStopsReorderCommand, saveCommand, deleteCommand } = commands;

class EditStop extends React.Component {
  constructor() {
    super();
    this.state = {
      stopDestination: {},
      finalDestination: {},
      originalStop: {},
    };
  }

  componentWillMount() {
    const { selectedPoi, stops } = this.props;
    this.setState({
      stopDestination: stops,
      finalDestination: selectedPoi,
      originalStop: stops,
    });
  }

  onDragEnd = () => {
    const { sendMessageToSocket } = this.props;
    const { finalDestination, originalStop } = this.state;
    this.setState({
      finalDestination: originalStop,
      stopDestination: finalDestination,
      originalStop: finalDestination,
    });
    sendMessageToSocket(editStopsReorderCommand);
  };

  onClickSave = () => {
    const {
      saveNewStops,
      saveNewDestination,
      sendMessageToSocket,
      removeStop,
      addStopInProgress,
    } = this.props;
    const { finalDestination, stopDestination } = this.state;
    if (stopDestination === undefined || Object.keys(stopDestination).length === 0) {
      removeStop({});
      addStopInProgress(false);
    }
    if (finalDestination === undefined || Object.keys(stopDestination).length === 0) {
      saveNewDestination(stopDestination);
      removeStop({});
      addStopInProgress(false);
    } else {
      saveNewStops(stopDestination);
      saveNewDestination(finalDestination);
    }
    sendMessageToSocket(saveCommand);
    navigate('/waypoint-details');
  };

  onClickDelete = (type, index) => {
    const { sendMessageToSocket } = this.props;
    const { stopDestination } = this.state;
    switch (type) {
      case 'stopDestination':
        this.setState({
          stopDestination: {},
        });
        sendMessageToSocket(deleteCommand(index));
        break;
      case 'finalDestination':
        this.setState({
          finalDestination: stopDestination,
          stopDestination: {},
        });
        sendMessageToSocket(deleteCommand(index));
        break;
      default:
    }
  };

  render() {
    const { classes, t } = this.props;
    const { finalDestination, stopDestination } = this.state;
    return (
      <React.Fragment>
        <Header
          title={t('poi-details.label.action-button.edit-stop')}
          subtitle={t('edit-stop.label.total-time')}
        />
        <Divider />
        <DragDropContext onDragEnd={this.onDragEnd}>
          <Droppable droppableId="droppable">
            {provided => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {stopDestination === undefined ||
                Object.keys(stopDestination).length === 0 ? null : (
                  <Draggable draggableId="stops" index={0}>
                    {provided => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <List>
                          <ListItem className={classes.list__container}>
                            <ListItemIcon>
                              <i className={`material-icons ${classes.menu__icon}`}>
                                view_headline
                              </i>
                            </ListItemIcon>
                            <ListItemIcon>
                              <i className={`material-icons ${classes.position__icon}`}>
                                supervised_user_circle
                              </i>
                            </ListItemIcon>
                            <ListItemText
                              className={classes.list__title}
                              primary={stopDestination.name}
                            />
                            <ListItemIcon onClick={() => this.onClickDelete('stopDestination', 0)}>
                              <i className={`material-icons ${classes.remove_icon}`}>
                                remove_circle
                              </i>
                            </ListItemIcon>
                          </ListItem>
                        </List>
                      </div>
                    )}
                  </Draggable>
                )}
                {finalDestination === undefined ||
                Object.keys(finalDestination).length === 0 ? null : (
                  <Draggable
                    draggableId="selectedPoi"
                    index={1}
                    isDragDisabled={Object.keys(stopDestination).length === 0}
                  >
                    {provided => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                      >
                        <List>
                          <ListItem className={classes.list__container}>
                            <ListItemIcon>
                              <i className={`material-icons ${classes.menu__icon}`}>
                                view_headline
                              </i>
                            </ListItemIcon>
                            <ListItemIcon>
                              <i className={`material-icons ${classes.position__icon}`}>
                                outlined_flag
                              </i>
                            </ListItemIcon>
                            <ListItemText
                              className={classes.list__title}
                              primary={finalDestination.name}
                            />
                            {Object.keys(stopDestination).length !== 0 ? (
                              <ListItemIcon
                                onClick={() => this.onClickDelete('finalDestination', 1)}
                              >
                                <i className={`material-icons ${classes.remove_icon}`}>
                                  remove_circle
                                </i>
                              </ListItemIcon>
                            ) : null}
                          </ListItem>
                        </List>
                      </div>
                    )}
                  </Draggable>
                )}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <div className={classes.footer}>
          <Button onClick={this.onClickSave} className={classes.save__button}>
            <Typography className={classes.save__text}>
              {t('edit-stop.label.action-button.save')}
            </Typography>
          </Button>
        </div>
      </React.Fragment>
    );
  }
}

EditStop.propTypes = {
  classes: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  selectedPoi: PropTypes.object,
  stops: PropTypes.object,
  saveNewDestination: PropTypes.func.isRequired,
  saveNewStops: PropTypes.func.isRequired,
  sendMessageToSocket: PropTypes.func.isRequired,
  removeStop: PropTypes.func.isRequired,
  addStopInProgress: PropTypes.func.isRequired,
};

EditStop.defaultProps = {
  stops: {},
  selectedPoi: {},
};

const mapStateToProps = ({ poi }) => ({
  selectedPoi: poi.selected_poi,
  stops: poi.stops,
});

const mapDispatchToProps = dispatch => ({
  saveNewDestination: payload => dispatch({ type: POI_ACTION_TYPES.POI_SELECTED.LOADING, payload }),
  saveNewStops: payload => dispatch({ type: POI_ACTION_TYPES.STOPS, payload }),
  sendMessageToSocket: payload => dispatch({ type: WS_ACTION_TYPES.WS.ON_COMMANDS, payload }),
  removeStop: () => dispatch({ type: POI_ACTION_TYPES.REMOVE_STOPS }),
  addStopInProgress: payload =>
    dispatch({ type: POI_ACTION_TYPES.NAVIGATION_STATE.ADD_STOP_INPROGRESS, payload }),
});

export default withStyles(styles)(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(withTranslation()(EditStop)),
);
